import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTraceableCommentCount,
  updateTraceableCommentCount,
} from 'services/traceable-comment';
import { useCallback, useRef } from 'react';
import { useCheckIsSuperAdmin, useCheckMemberSupplier } from 'hooks';
import { useCheckAllowViewQaCommentWorkflow } from 'pages/qa-spec/hooks';
import { useCallback, useRef } from 'react';

export const useTraceableCommentCount = ({ productId, ownerId }) => {
  const isQAUser = useCheckAllowViewQaCommentWorkflow();
  const isSupplierUser = useCheckMemberSupplier();

  const shouldFetch = !!productId && !!ownerId && (isQAUser || isSupplierUser);

  const { data, isLoading, error } = useQuery(
    ['traceable-comment-count', productId, ownerId],
    () =>
      getTraceableCommentCount({
        ProductId: productId,
        OwnerId: ownerId,
        IsSupplier: isSupplierUser,
      }),
    {
      enabled: shouldFetch,
      select: (res) => res?.data?.count,
      staleTime: 0,
    }
  );

  return {
    count: data ?? 0,
    isLoading,
    error,
  };
};

export const useUpdateCommentCountOnTabClick = ({ productId, ownerId }) => {
  const queryClient = useQueryClient();
  const isQAUser = useCheckAllowViewQaCommentWorkflow();
  const isSupplierUser = useCheckMemberSupplier();
  const isSuperAdmin = useCheckIsSuperAdmin();

  const hasCalled = useRef(false); // prevent multiple calls

  const handleCommentsTabClick = useCallback(() => {
    if (!productId || !ownerId || hasCalled.current) return;

    const isAllowed = (isQAUser || isSupplierUser) && !isSuperAdmin;

    if (isAllowed) {
      updateTraceableCommentCount({
        ProductId: productId,
        OwnerId: ownerId,
        IsSupplier: isSupplierUser,
      }).then(() => {
        queryClient.invalidateQueries([
          'traceable-comment-count',
          productId,
          ownerId,
        ]);
      });

      hasCalled.current = true; // ensure it's called only once per mount
    }
  }, [productId, ownerId, isQAUser, isSupplierUser]);

  return { handleCommentsTabClick };
};
